interface LogEntry {
  id: number;
  timestamp: string;
  level: "info" | "warn" | "error";
  message: string;
  source?: string;
  verbose?: boolean;
}

let logId = 0;
const logBuffer: LogEntry[] = [];
const MAX_LOG_BUFFER = 1000;
const logSubscribers = new Set<any>();

function parseBooleanQuery(value: string | undefined): boolean {
  if (!value) return false;
  return value === "1" || value.toLowerCase() === "true" || value.toLowerCase() === "yes";
}

export function detectLogSource(message: string): LogEntry["source"] {
  if (message.includes("[workflow:verbose]") || message.includes("[workflow]")) return "workflow";
  if (message.includes("[scheduler]")) return "scheduler";
  if (message.includes("[cron]") || message.includes("[crons]") || message.includes("[usage-cron]")) {
    return "cron";
  }
  if (message.includes("[notification]")) return "notification";
  if (message.includes("-->") || message.includes("<--")) return "http";
  return undefined;
}

export function isVerboseLogEntry(
  message: string,
  level: LogEntry["level"],
  source: LogEntry["source"],
): boolean {
  if (message.includes("[workflow:verbose]")) return true;
  if (source === "http") return true;

  if (source === "scheduler") {
    return (
      message.includes('Scheduled "') ||
      message.includes('Persisted "') ||
      message.includes("Cancelled job ") ||
      message.includes('Registered cron "') ||
      (message.includes('Cron "') && message.includes(" triggered")) ||
      message.includes('onError handler "') ||
      (level === "info" && message.includes('Action "') && message.includes(" completed"))
    );
  }

  if (source === "cron") {
    return (
      message.includes("No due jobs") ||
      message.includes(" due ") ||
      message.includes("apps need wake") ||
      message.includes(": restoring (") ||
      message.includes("DB-based provision success") ||
      (level === "info" && message.includes(": success ("))
    );
  }

  return false;
}

export function subscribeToLogs(ws: any) {
  logSubscribers.add(ws);
  try {
    ws.send(JSON.stringify({ type: "log:subscribed" }));
  } catch {
    /* ignore */
  }
}

export function unsubscribeFromLogs(ws: any) {
  logSubscribers.delete(ws);
}

function pushLog(level: LogEntry["level"], args: any[]) {
  const message = args.map((arg) => (typeof arg === "string" ? arg : JSON.stringify(arg))).join(" ");
  const source = detectLogSource(message);
  const entry: LogEntry = {
    id: ++logId,
    timestamp: new Date().toISOString(),
    level,
    message,
    verbose: isVerboseLogEntry(message, level, source),
    source,
  };

  logBuffer.push(entry);
  if (logBuffer.length > MAX_LOG_BUFFER) {
    logBuffer.splice(0, logBuffer.length - MAX_LOG_BUFFER);
  }

  if (logSubscribers.size > 0) {
    const payload = JSON.stringify({ type: "log:entry", ...entry });
    for (const ws of logSubscribers) {
      try {
        ws.send(payload);
      } catch {
        logSubscribers.delete(ws);
      }
    }
  }
}

const originalConsoleLog = console.log;
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

console.log = (...args: any[]) => {
  pushLog("info", args);
  originalConsoleLog.apply(console, args);
};
console.warn = (...args: any[]) => {
  pushLog("warn", args);
  originalConsoleWarn.apply(console, args);
};
console.error = (...args: any[]) => {
  pushLog("error", args);
  originalConsoleError.apply(console, args);
};

export function getLogBuffer(limit: number, level: string, includeVerbose: boolean): LogEntry[] {
  let filtered = logBuffer;
  if (level !== "all") {
    filtered = filtered.filter((entry) => entry.level === level);
  }
  if (!includeVerbose) {
    filtered = filtered.filter((entry) => !entry.verbose);
  }
  return filtered.slice(-limit).reverse();
}

export function createAdminLogsHandler() {
  return (c: any) => {
    const limit = Number(c.req.query("limit") || "100");
    const level = c.req.query("level") || "all";
    const verbose = parseBooleanQuery(c.req.query("verbose"));
    return c.json(getLogBuffer(limit, level, verbose));
  };
}
